// @ts-strict-ignore
import {AfterViewInit, Component, computed, effect, inject, Input, output, signal} from "@angular/core";
import {
	dateHelperEvent_getEventDays,
	dateHelperEvent_sameDay,
	Event,
	EventColors,
	EventState,
	EventType,
	SessionState,
	SimStatusPalette,
	TrainingSession,
	UHOpen,
	VMEvent,
	VMEventDay,
} from "@models";
import {EVENT_ACTION_BUTTONS} from "../event-selection/event-selection.component";
import {SimEventService, SimMaterialDialogService, NotificationService, SimRecordingsService, TypePredicates} from "@services";
import {Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";
import {EventEmitterService} from "@event-emitter";
import {CustomColorMapping} from "../../models/SimEventPalette";
import {EMPTY, from, mergeMap} from "rxjs";
import {MenuAction} from "@common/ui/action-menu/action-menu.component";

@Component({
	selector: "sim-event-container",
	templateUrl: "./event-container.component.html",
	styleUrls: ["./event-container.component.scss"],
	providers: [EventEmitterService],
	standalone: false,
})
export class EventContainerComponent implements AfterViewInit {
	// Input
	@Input() enableSelection: boolean = false;
	@Input() hideDraftSession: boolean = true;
	@Input() showJoinStartActions: boolean = false;
	@Input() includeEmptyTrainingSessions: boolean = true;
	@Input() defaultOpen: boolean = false;
	@Input() recordingSelectable: boolean = false;
	@Input() showOnlyRecordedScenarios: boolean = false;
	@Input() isArchive: boolean = false;
	@Input() eventActions: EVENT_ACTION_BUTTONS = null;
	@Input() showExports: boolean = false;
	@Input() recordingOrder: "asc" | "desc" = "desc";
	@Input() creationMode: boolean = false;
	@Input() isExamSchedule: boolean = false;

	@Input({required: true, alias: "event"}) set inputEvent(value: Event & UHOpen & VMEvent) {
		this.event.set(value);
	}

	// Output
	selectRecording = output<string>();
	eventSelected = output<Event & UHOpen & VMEvent>();
	eventOpened = output<Event & UHOpen & VMEvent>();
	deleteEventEmitter = output<Event>();

	// Properties
	public loadingEventOpen = true;
	public totalSessionCount = computed<number>(() => this._computeTotalSessionCount());
	public currentDay = signal<VMEventDay | null>(null);
	public event = signal<Event & UHOpen & VMEvent>(null);
	protected menuActions: MenuAction[] = [];

	// Readonly
	public readonly T = Translations;
	protected readonly EventState = EventState;
	protected readonly EventColors = EventColors;
	protected readonly SimStatusPalette = SimStatusPalette;
	protected readonly EventColorMapping = CustomColorMapping;

	// Injections
	public translate = inject(TranslateService);
	private _router = inject(Router);
	private _simEventService = inject(SimEventService);
	private _simRecordingService = inject(SimRecordingsService);
	private _simDialogService = inject(SimMaterialDialogService);
	private _eventEmitterService = inject(EventEmitterService);
	private _simNotificationService = inject(NotificationService);

	constructor() {
		this._eventEmitterService.onEventUpdated.subscribe((eventToUpdate) => {
			if (this.event().id === eventToUpdate.eventInfo.id) {
				let generateDaysNew = false;
				let eventWasOpen = false;
				if (this.event().days?.length >= 0) {
					generateDaysNew = true;
				}
				if (this.event().open) {
					eventWasOpen = true;
				}
				this.event.set(<Event & UHOpen & VMEvent>eventToUpdate.eventInfo);
				if (generateDaysNew) {
					this._hydrateEvent();
				}
				if (eventWasOpen) {
					this.event().open = true;
				}
			}
		});

		this._eventEmitterService.onNotifyExport.subscribe(async (data) => {
			const newEvent = await this._simEventService.getEventByScenarioRecordingId(data.scenarioRecordingId);
			if (this.event().id === newEvent.id) {
				this.event().finishedExportsCount = newEvent.finishedExportsCount;
			}
		});

		effect(() => this._effectBuildEventActions());
	}

	public toggleEventOpen(): void {
		this.loadingEventOpen = true;
		this.event().open = !this.event().open;

		if (this.event().open === false || this.event().days) {
			this.loadingEventOpen = false;
			return;
		}

		this._hydrateEvent();
	}

	public ngAfterViewInit(): void {
		if (this.defaultOpen && (this.event().open === false || this.event().open === undefined)) {
			this.toggleEventOpen();
		}
	}

	protected isSameDay(dateA: string | Date, dateB: string | Date): boolean {
		const convertedDateA = new Date(dateA);
		const convertedDateB = new Date(dateB);
		return (
			convertedDateA.getFullYear() === convertedDateB.getFullYear() &&
			convertedDateA.getMonth() === convertedDateB.getMonth() &&
			convertedDateA.getDate() === convertedDateB.getDate()
		);
	}

	protected editEvent(): void {
		const event = this.event();
		this._router.navigate(["/web-manager/list/event-manager", event.id]).then();
	}

	protected deleteEvent(): void {
		this.deleteEventEmitter.emit(this.event());
	}

	protected toggleArchiveEvent(): void {
		if (this.event().state === EventState.Archived) {
			this._reopenEvent();
		} else {
			this._archiveEvent();
		}
	}

	protected openExportEventDialog() {
		this._simDialogService.openEventExportDialog({eventId: this.event().id, eventIdList: null}).then();
	}

	protected copyEvent() {
		const event = this.event();
		this._simDialogService
			.openCopyEventDialog({
				eventId: event.id,
				eventType: event.eventType,
			})
			.then(({action, ...result}) => {
				if (action === "ok") {
					this._simEventService.copyEvent({...result}).then((eventCopyResult) => {
						if (eventCopyResult.success) {
							this._simNotificationService.notifySuccess("Event Successfully copied");
							setTimeout(() => {
								this._router.navigate(["/web-manager/list/event-manager/create/", eventCopyResult.newEventId]).then();
							}, 1500);
						}
					});
				}
			});
	}

	protected changeOwnership() {
		const event = this.event();
		if (!event) {
			return;
		}
		this._simDialogService
			.openUpdateOwnershipDialog({
				type: "Event",
				id: event.id,
				currentOwnerId: event.ownerUserId,
			})
			.then();
	}

	private _archiveEvent() {
		const event = this.event();
		from(
			this._simEventService.upSertEventState({
				eventId: event.id,
				eventState: EventState.Archived,
			}),
		)
			.pipe(
				mergeMap(() =>
					this._simEventService.upSertEventState({
						eventId: event.id,
						eventState: EventState.Archived,
					}),
				),
				mergeMap(() => {
					this.event.update((value) => {
						return {...value, state: EventState.Archived};
					});
					this._simNotificationService.notifySuccess("Event Archived");
					return EMPTY;
				}),
			)
			.subscribe(() => this._setTrainingSessionLockState(event.sessionIds, "lock"));
	}

	private _reopenEvent() {
		from(
			this._simDialogService.openConfirmationDialog({
				title: this.translate.instant(this.T.EVENT_REOPEN_CONFIRMATION_DIALOG_TITLE),
				description: this.translate.instant(this.T.EVENT_REOPEN_CONFIRMATION_DIALOG_TEXT),
				okText: this.translate.instant(this.T.EVENT_REOPEN_CONFIRMATION_DIALOG_OK),
				cancelText: this.translate.instant(this.T.EVENT_REOPEN_CONFIRMATION_DIALOG_CANCEL),
			}),
		)
			.pipe(
				mergeMap((confirmationResponse) => {
					if (confirmationResponse.action !== "ok") {
						return EMPTY;
					}
					return this._simEventService.upSertEventState({
						eventId: this.event().id,
						eventState: EventState.Inactive,
					});
				}),
				mergeMap(() => {
					this.event.update((value) => {
						return {...value, state: EventState.Inactive};
					});
					this._simNotificationService.notifySuccess("Event Reopened");
					return EMPTY;
				}),
			)
			.subscribe(() => this._setTrainingSessionLockState(this.event().sessionIds, "unlock"));
	}

	private _hydrateEvent(): void {
		const sessionState = this.isExamSchedule ? SessionState.Scheduled : this.isArchive ? SessionState.Finished : null;
		this._simEventService.getEventDetailById(this.event().id, sessionState).then((eventDetail) => {
			const days = dateHelperEvent_getEventDays(this.event());
			for (const day of days) {
				const searchDayForSessions = day.date;

				eventDetail.scenarioRecordings.forEach((scenarioRecording, index) => {
					const foundSession = eventDetail.trainingSessions.find(
						(trainingSession) => trainingSession.id === scenarioRecording.trainingSessionId,
					);
					if (!TypePredicates.isTeamTrainingSession(foundSession)) {
						return;
					}
					const elementPos = foundSession.scenarioRecordingGroups
						.map((x) => {
							return x.scenarioRecordingGroupId;
						})
						.indexOf(scenarioRecording.scenarioRecordingGroupId);
					if (foundSession["scenarioRecordings"]) {
						foundSession.scenarioRecordingGroups[elementPos]["scenarioRecordings"].push(scenarioRecording);
					} else {
						foundSession.scenarioRecordingGroups[elementPos]["scenarioRecordings"] = [scenarioRecording];
					}
				});

				// Hydrate sessions to days
				day.sessions = eventDetail.trainingSessions.filter((session: TrainingSession) => {
					return dateHelperEvent_sameDay(new Date(session.startTime), new Date(searchDayForSessions));
				});

				setTimeout(() => {
					this.loadingEventOpen = false;
				}, 350);
			}

			// Trigger dependent signals
			this.event.update((value) => {
				return {...value, days};
			});
		});
	}

	private async _setTrainingSessionLockState(sessionIds: string[], action: "lock" | "unlock") {
		const tasks: Promise<unknown>[] = [];
		for (const sessionId of sessionIds) {
			tasks.push(
				this._simRecordingService.setTrainingSessionLock({
					trainingSessionId: sessionId,
					newLockState: action === "lock",
				}),
			);
		}
		await Promise.all(tasks);
	}

	private _effectBuildEventActions(): void {
		if (!this.event()) {
			return;
		}
		this.menuActions = [
			{
				fontIcon: this.event().state === EventState.Archived ? "fa-unlock-alt" : "fa-lock-alt",
				labelKey:
					this.event().state === EventState.Archived
						? this.T.SHARED_EVENT_CONTAINER_REOPEN_EVENT
						: this.T.SHARED_EVENT_CONTAINER_ARCHIVE_EVENT,
				click: () => this.toggleArchiveEvent(),
				condition: this.eventActions?.lockEventBtn,
				permission: {
					permissionObj: this.event(),
					hasDataPermission: "Edit",
				},
			},
			{
				fontIcon: "fa-download",
				labelKey: this.T.TOOLTIP_EXPORT_EVENT,
				permission: {
					permissionObj: this.event(),
					hasDataPermission: "Export",
				},
				click: () => this.openExportEventDialog(),
			},
			{
				fontIcon: "fa-download",
				labelKey: this.T.EXPORT_ASSESSMENT_OVERVIEW,
				condition: this.event().eventType === EventType.Exam,
				permission: {
					permissionObj: this.event(),
					hasDataPermission: "Export",
				},
				click: () => this._exportAssessmentOverview(),
			},
			{
				fontIcon: "fa-copy",
				labelKey: this.T.COPY_EVENT_TOOLTIP,
				condition: this.eventActions?.copyEventButton,
				permission: {
					permissionObj: this.event(),
					hasDataPermission: "Duplicate",
				},
				click: () => this.copyEvent(),
			},
			{
				fontIcon: "fa-user",
				labelKey: this.T.CHANGE_OWNERSHIP,
				click: () => this.changeOwnership(),
				condition: this.eventActions?.changeOwnerShipBtn,
				permission: {
					permissionObj: this.event(),
					hasDataPermission: "Edit",
				},
			},
			{
				fontIcon: "fa-edit",
				labelKey: this.T.EDIT_EVENT_TOOLTIP,
				click: () => this.editEvent(),
				permission: {
					permissionObj: this.event(),
					hasDataPermission: "Edit",
				},
				condition: this.eventActions?.editEventBtn,
			},
		];
	}

	private _computeTotalSessionCount(): number {
		// TODO: we are planning general changes to the event creation page and the respective model.
		//  For now, we are referencing the count of the event's sessionIds, which is not strictly correct, because it can include aborted and deleted sessions.
		return this.event()?.sessionIds?.length;
	}

	private _exportAssessmentOverview() {
		window.location.href = this._simEventService.getExamEventAssessmentOverview_Url([this.event().id]);
	}
}
